/**
 * @description Derived Scope 1 highlights and rail. Templates only. No LLM.
 *
 * @dependencies ./scope1Dashboard, ./scope1Pace
 *
 * @notes Built each load from aggregates + today's jobs. Not stored. Usual
 *   day still draws three from the pool when three real facts exist.
 *   Pace is en route through this hour vs the last 12 same weekdays.
 *   Highlights stay today and do not invent filler. Grid follows the
 *   filter: account share, off pace,
 *   then rotating slowest tows / vehicle mix / busiest driver / busiest
 *   reason. A Usual-only tile does not take a slot. Same fact does not
 *   appear in both at once.
 */

import {
  countByJobField,
  formatSeconds,
  formatVolume,
  isoWeekdayInTimeZone,
  lateRate,
  lateRateByType,
  meanDuration,
  RESPONSE_ON_SCENE,
  sumMeasure,
  topCount,
  weekdayName
} from './scope1Dashboard';
import {
  MIN_HIGHLIGHT_TOWS,
  accountShareStat,
  busiestDriverStat,
  busiestReasonStat,
  currentActivityLine,
  jobsForResponse,
  offPaceStat,
  pacePosition,
  slowestTowsStat,
  vehicleMixStat,
  volumeAttentionLine
} from './scope1Pace';

export const HIGHLIGHT_UNUSUAL = 0.10;

/**
 * @param {number} today
 * @param {number} usual
 * @returns {number}
 */
export function distanceFromUsual(today, usual) {
  const current = Number(today) || 0;
  const baseline = Number(usual) || 0;
  if (baseline === 0) return current === 0 ? 0 : 1;
  return Math.abs(current - baseline) / baseline;
}

/**
 * @param {Array<object>} pool
 * @param {number} count
 * @param {() => number} [random]
 * @returns {Array<object>}
 */
export function pickFromPool(pool, count = 3, random = Math.random) {
  const copy = [...(pool || [])];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, Math.min(count, copy.length));
}

/**
 * Rank unusual first. Always three facts when the pool can fill them.
 *
 * @param {Array<object>} pool
 * @param {{ threshold?: number, random?: () => number }} [opts]
 * @returns {Array<object>}
 */
export function selectHighlights(pool, { threshold = HIGHLIGHT_UNUSUAL, random = Math.random } = {}) {
  const facts = (pool || []).filter(Boolean);
  if (facts.length === 0) return [];
  const ranked = [...facts].sort((a, b) => (b.distance || 0) - (a.distance || 0));
  const unusual = ranked.filter((row) => (row.distance || 0) >= threshold);
  const picked = [];
  const used = new Set();
  for (const row of unusual) {
    if (picked.length >= 3) break;
    picked.push(row);
    used.add(row.id);
  }
  if (picked.length < 3) {
    const rest = facts.filter((row) => !used.has(row.id));
    for (const row of pickFromPool(rest, 3 - picked.length, random)) {
      picked.push(row);
    }
  }
  return picked.slice(0, 3);
}

/**
 * Quiet fillers so Today's highlights is never empty.
 *
 * @param {number} weekday
 * @returns {Array<object>}
 */
export function quietHighlightFallback(weekday) {
  void weekday;
  return [];
}

/**
 * Hold unusual callouts until there are enough tows. Quieter facts still
 * fill the board.
 *
 * @param {Array<object>} pool
 * @param {number} sample
 * @param {{ threshold?: number, random?: () => number, weekday?: number }} [opts]
 * @returns {Array<object>}
 */
export function selectHighlightsWhenReady(pool, sample, opts) {
  const facts = (pool || []).filter(Boolean);
  if (!Number.isFinite(Number(sample)) || Number(sample) < MIN_HIGHLIGHT_TOWS) {
    const quiet = facts.filter((row) => (row.distance || 0) < HIGHLIGHT_UNUSUAL);
    return selectHighlights(quiet.length >= 3 ? quiet : facts, {
      ...opts,
      threshold: Number.POSITIVE_INFINITY
    });
  }
  return selectHighlights(facts, opts);
}

/**
 * Volume against a usual day, through this hour.
 *
 * @param {object} input
 * @returns {object|null}
 */
export function volumeHighlight({ todayTotal, usualTotal, weekday, pace } = {}) {
  const day = weekdayName(weekday);
  if (pace) {
    if (pace.position === 'unknown') return null;
    if (pace.position === 'behind') {
      return {
        id: 'volume',
        text: volumeAttentionLine(pace) || `${formatVolume(pace.current)} tows. Behind a usual ${day}.`,
        value: formatVolume(pace.current),
        distance: distanceFromUsual(pace.current, pace.usual)
      };
    }
    if (pace.position === 'on') {
      return {
        id: 'volume',
        text: `Tows are in line with a usual ${day}.`,
        value: formatVolume(pace.current),
        distance: 0
      };
    }
    const through = pace.live && pace.throughLabel ? ` through ${pace.throughLabel}` : '';
    return {
      id: 'volume',
      text: `${formatVolume(pace.current)} tows${through}. Ahead of a usual ${day}.`,
      value: formatVolume(pace.current),
      distance: distanceFromUsual(pace.current, pace.usual)
    };
  }
  if (todayTotal == null || usualTotal == null) return null;
  const usual = Number(usualTotal);
  const band = {
    usual,
    low: usual * (1 - HIGHLIGHT_UNUSUAL),
    high: usual * (1 + HIGHLIGHT_UNUSUAL)
  };
  const position = pacePosition(todayTotal, band);
  if (position === 'unknown') return null;
  const today = Number(todayTotal) || 0;
  if (position === 'behind') {
    return {
      id: 'volume',
      text: `${formatVolume(today)} tows. Behind a usual ${day}.`,
      value: formatVolume(today),
      distance: distanceFromUsual(today, usualTotal)
    };
  }
  if (position === 'on') {
    return { id: 'volume', text: `Tows are in line with a usual ${day}.`, value: formatVolume(today), distance: 0 };
  }
  return {
    id: 'volume',
    text: `${formatVolume(today)} tows. Ahead of a usual ${day}.`,
    value: formatVolume(today),
    distance: distanceFromUsual(today, usualTotal)
  };
}

/**
 * @param {object} input
 * @returns {object|null}
 */
export function responseHighlight({ todaySeconds, usualSeconds, weekday }) {
  if (todaySeconds == null || usualSeconds == null) return null;
  const day = weekdayName(weekday);
  const distance = distanceFromUsual(todaySeconds, usualSeconds);
  const delta = Math.round((Number(todaySeconds) - Number(usualSeconds)) / 60);
  let text;
  if (distance < HIGHLIGHT_UNUSUAL) {
    text = `Response time is in line with a usual ${day}.`;
  } else if (delta > 0) {
    text = `Average response is ${Math.abs(delta)} min slower than a usual ${day}.`;
  } else {
    text = `Average response is ${Math.abs(delta)} min faster than a usual ${day}.`;
  }
  return { id: 'response', text, value: formatSeconds(todaySeconds), distance };
}

/**
 * Counts with every percent. Usual late uses the same minutes as today.
 *
 * @param {object} input
 * @returns {object|null}
 */
export function lateHighlight({ todayRate, usualRate, weekday, todayLate, todaySample }) {
  if (todayRate == null || usualRate == null) return null;
  const day = weekdayName(weekday);
  const todayPct = Math.round(Number(todayRate) * 100);
  const usualPct = Math.round(Number(usualRate) * 100);
  const distance = Math.abs(Number(todayRate) - Number(usualRate));
  const counts = todayLate != null && todaySample != null
    ? `late on ${todayLate} of ${todaySample} arrivals`
    : '';
  let text;
  if (distance <= HIGHLIGHT_UNUSUAL) {
    text = counts
      ? `% late is in line with a usual ${day} (${counts}).`
      : `% late is in line with a usual ${day}.`;
  } else {
    text = counts
      ? `Late tows are ${todayPct}%, ${counts}, vs ${usualPct}% on a usual ${day}.`
      : `Late tows are ${todayPct}%, vs ${usualPct}% on a usual ${day}.`;
  }
  return { id: 'late', text, value: `${todayPct}%`, distance };
}

/**
 * @param {object} input
 * @returns {object|null}
 */
export function classMixHighlight({ todayJobs, usualJobs, weekday, mixOff }) {
  void todayJobs;
  void usualJobs;
  if (mixOff) {
    return {
      id: 'classMix',
      text: mixOff.sentence,
      value: `${Math.round(mixOff.todayPct)}%`,
      distance: mixOff.delta / 100
    };
  }
  const day = weekdayName(weekday);
  return {
    id: 'classMix',
    text: `Vehicle mix is in line with a usual ${day}.`,
    value: '—',
    distance: 0
  };
}

/**
 * Share against share. Counts with every percent. Reason uses a labeled
 * today-vs-weekday sentence. Account and other fields keep the share line.
 *
 * @param {object} input
 * @returns {object|null}
 */
export function topFieldHighlight({
  id,
  todayJobs,
  usualJobs,
  field,
  weekday,
  noun
}) {
  void noun;
  const todayCounts = countByJobField(todayJobs, field);
  const usualCounts = countByJobField(usualJobs, field);
  const top = topCount(todayCounts);
  if (!top) return null;
  const day = weekdayName(weekday);
  let todayTotal = 0;
  let usualTotal = 0;
  for (const count of todayCounts.values()) todayTotal += count;
  for (const count of usualCounts.values()) usualTotal += count;
  const usualCount = usualCounts.get(top.key) || 0;
  const todayShare = todayTotal ? top.count / todayTotal : 0;
  const usualShare = usualTotal ? usualCount / usualTotal : 0;
  const distance = Math.abs(todayShare - usualShare);
  const todayPct = Math.round(todayShare * 100);
  const usualPct = Math.round(usualShare * 100);
  const text = field === 'reason'
    ? `Reason: ${top.key}, ${top.count} of today's ${todayTotal} tows (${todayPct}%). Usual ${day} is ${usualPct}%.`
    : distance <= HIGHLIGHT_UNUSUAL
      ? `${top.key} share is in line with a usual ${day}.`
      : `${top.key} is ${top.count} of ${todayTotal}, ${todayPct}%. Usual is ${usualPct}%.`;
  return { id, text, value: `${todayPct}%`, distance, name: top.key };
}

/**
 * @param {object} input
 * @returns {object|null}
 */
export function topDriverHighlight({ todayJobs } = {}) {
  const counts = countByJobField(todayJobs, 'driver');
  const ranked = [...counts.entries()].sort((a, b) => (
    b[1] - a[1] || String(a[0]).localeCompare(String(b[0]))
  ));
  if (ranked.length === 0) return null;
  let total = 0;
  for (const count of counts.values()) total += count;
  const [name, count] = ranked[0];
  const nextCount = ranked[1] ? ranked[1][1] : 0;
  const distance = total ? (count - nextCount) / total : 0;
  return {
    id: 'topDriver',
    name,
    text: `${name} has ${count} of ${total}. Next has ${nextCount}.`,
    value: String(count),
    distance
  };
}

/**
 * @param {object} input
 * @returns {object|null}
 */
export function companyVolumeHighlight({ name, todayTotal, usualTotal, weekday, pace } = {}) {
  if (!name) return null;
  const day = weekdayName(weekday);
  if (pace) {
    if (pace.position === 'unknown') return null;
    if (pace.position === 'behind') {
      return {
        id: `companyBehind:${name}`,
        text: `${name} is behind a usual ${day}.`,
        value: formatVolume(pace.current),
        distance: distanceFromUsual(pace.current, pace.usual),
        name
      };
    }
    if (pace.position === 'on') {
      return {
        id: `companyVolume:${name}`,
        text: `${name} volume is in line with a usual ${day}.`,
        value: formatVolume(pace.current),
        distance: 0,
        name
      };
    }
    return {
      id: `companyVolume:${name}`,
      text: `${name} is ahead of a usual ${day}.`,
      value: formatVolume(pace.current),
      distance: distanceFromUsual(pace.current, pace.usual),
      name
    };
  }
  if (todayTotal == null || usualTotal == null) return null;
  const usual = Number(usualTotal);
  const band = {
    usual,
    low: usual * (1 - HIGHLIGHT_UNUSUAL),
    high: usual * (1 + HIGHLIGHT_UNUSUAL)
  };
  const position = pacePosition(todayTotal, band);
  if (position === 'unknown') return null;
  if (position === 'behind') {
    return {
      id: `companyBehind:${name}`,
      text: `${name} is behind a usual ${day}.`,
      value: formatVolume(todayTotal),
      distance: distanceFromUsual(todayTotal, usualTotal),
      name
    };
  }
  const text = position === 'on'
    ? `${name} volume is in line with a usual ${day}.`
    : `${name} is ahead of a usual ${day}.`;
  return {
    id: `companyVolume:${name}`,
    text,
    value: formatVolume(todayTotal),
    distance: position === 'on' ? 0 : distanceFromUsual(todayTotal, usualTotal),
    name
  };
}

/**
 * Share against share. Counts with every percent.
 *
 * @param {object} input
 * @returns {object|null}
 */
export function companyShareHighlight({
  name,
  todayCount,
  todayTotal,
  usualCount,
  usualTotal,
  todayShare,
  usualShare,
  weekday
}) {
  if (!name) return null;
  const day = weekdayName(weekday);
  const currentCount = todayCount != null ? Number(todayCount) : null;
  const currentTotal = todayTotal != null ? Number(todayTotal) : null;
  const share = currentTotal ? currentCount / currentTotal : Number(todayShare) || 0;
  const baseline = usualTotal ? Number(usualCount || 0) / Number(usualTotal) : Number(usualShare) || 0;
  const distance = Math.abs(share - baseline);
  const sharePct = Math.round(share * 100);
  const usualPct = Math.round(baseline * 100);
  const text = distance <= HIGHLIGHT_UNUSUAL
    ? `${name} share is in line with a usual ${day}.`
    : currentCount != null && currentTotal != null
      ? `${name} is ${currentCount} of ${currentTotal}, ${sharePct}%. Usual is ${usualPct}%.`
      : `${name} has ${sharePct}% of today's tows, vs ${usualPct}% on a usual ${day}.`;
  return { id: 'companyShare', text, value: `${sharePct}%`, distance, name };
}

/**
 * Company pool: volume, class mix, response, late, top account.
 *
 * @param {object} input
 * @returns {Array<object>}
 */
export function companyHighlightPool({
  todayTotal,
  usualTotal,
  todayJobs,
  usualJobs,
  weekday,
  metric = RESPONSE_ON_SCENE,
  thresholdSeconds,
  policeMinutes,
  regularMinutes,
  accountType,
  pace,
  mixOff
}) {
  const todayForResponse = jobsForResponse(todayJobs).jobs;
  const usualForResponse = jobsForResponse(usualJobs).jobs;
  const todayLate = policeMinutes != null && regularMinutes != null
    ? lateRateByType(todayForResponse, policeMinutes, regularMinutes, RESPONSE_ON_SCENE, accountType)
    : lateRate(todayForResponse, thresholdSeconds, RESPONSE_ON_SCENE);
  const usualLate = policeMinutes != null && regularMinutes != null
    ? lateRateByType(usualForResponse, policeMinutes, regularMinutes, RESPONSE_ON_SCENE, accountType)
    : lateRate(usualForResponse, thresholdSeconds, RESPONSE_ON_SCENE);
  return [
    volumeHighlight({ todayTotal, usualTotal, weekday, pace }),
    classMixHighlight({ todayJobs, usualJobs, weekday, mixOff }),
    responseHighlight({
      todaySeconds: meanDuration(todayForResponse, metric),
      usualSeconds: meanDuration(usualForResponse, metric),
      weekday
    }),
    lateHighlight({
      todayRate: todayLate.rate,
      usualRate: usualLate.rate,
      weekday,
      todayLate: todayLate.late,
      todaySample: todayLate.sample
    }),
    topFieldHighlight({
      id: 'topAccount',
      todayJobs,
      usualJobs,
      field: 'accountName',
      weekday,
      noun: 'tows'
    }),
    topDriverHighlight({ todayJobs }),
    topFieldHighlight({
      id: 'reason',
      todayJobs,
      usualJobs,
      field: 'reason',
      weekday,
      noun: 'tows'
    })
  ].filter(Boolean);
}

/**
 * All pool: company share, company volume, class mix.
 *
 * @param {object} input
 * @returns {Array<object>}
 */
export function allHighlightPool({
  companyToday,
  companyUsual,
  todayJobs,
  usualJobs,
  weekday,
  mixOff,
  companyPace,
  metric = RESPONSE_ON_SCENE,
  thresholdSeconds,
  policeMinutes,
  regularMinutes,
  accountType
}) {
  const totalOf = (row) => {
    if (row?.total != null) return Number(row.total) || 0;
    const buckets = row?.buckets || [];
    const completed = sumMeasure(buckets, 'completedCount');
    if (completed > 0) return completed;
    return sumMeasure(buckets, 'enrouteCount');
  };
  const todayEntries = (companyToday || [])
    .map((row) => ({ name: row.name, total: totalOf(row) }))
    .sort((a, b) => b.total - a.total);
  const usualByName = new Map(
    (companyUsual || []).map((row) => [row.name, totalOf(row)])
  );
  const todaySum = todayEntries.reduce((sum, row) => sum + row.total, 0);
  const usualSum = [...usualByName.values()].reduce((sum, value) => sum + value, 0);
  const busiest = todayEntries[0];
  const pool = [];
  if (busiest) {
    pool.push(companyVolumeHighlight({
      name: busiest.name,
      todayTotal: busiest.total,
      usualTotal: usualByName.get(busiest.name) || 0,
      weekday,
      pace: companyPace?.[busiest.name]
    }));
    pool.push(companyShareHighlight({
      name: busiest.name,
      todayCount: busiest.total,
      todayTotal: todaySum,
      usualCount: usualByName.get(busiest.name) || 0,
      usualTotal: usualSum,
      weekday
    }));
  }
  const named = new Set(pool.map((row) => row?.name).filter(Boolean));
  for (const row of todayEntries) {
    if (named.has(row.name)) continue;
    const behind = companyVolumeHighlight({
      name: row.name,
      todayTotal: row.total,
      usualTotal: usualByName.get(row.name) || 0,
      weekday,
      pace: companyPace?.[row.name]
    });
    if (behind?.id?.startsWith('companyBehind:')) {
      pool.push(behind);
      named.add(row.name);
    }
  }
  const todayForResponse = jobsForResponse(todayJobs).jobs;
  const usualForResponse = jobsForResponse(usualJobs).jobs;
  const todayLate = policeMinutes != null && regularMinutes != null
    ? lateRateByType(todayForResponse, policeMinutes, regularMinutes, RESPONSE_ON_SCENE, accountType)
    : lateRate(todayForResponse, thresholdSeconds, RESPONSE_ON_SCENE);
  const usualLate = policeMinutes != null && regularMinutes != null
    ? lateRateByType(usualForResponse, policeMinutes, regularMinutes, RESPONSE_ON_SCENE, accountType)
    : lateRate(usualForResponse, thresholdSeconds, RESPONSE_ON_SCENE);
  pool.push(classMixHighlight({ todayJobs, usualJobs, weekday, mixOff }));
  pool.push(responseHighlight({
    todaySeconds: meanDuration(todayForResponse, metric),
    usualSeconds: meanDuration(usualForResponse, metric),
    weekday
  }));
  pool.push(lateHighlight({
    todayRate: todayLate.rate,
    usualRate: usualLate.rate,
    weekday,
    todayLate: todayLate.late,
    todaySample: todayLate.sample
  }));
  pool.push(topFieldHighlight({
    id: 'topAccount',
    todayJobs,
    usualJobs,
    field: 'accountName',
    weekday,
    noun: 'tows'
  }));
  pool.push(topDriverHighlight({ todayJobs }));
  pool.push(topFieldHighlight({
    id: 'reason',
    todayJobs,
    usualJobs,
    field: 'reason',
    weekday,
    noun: 'tows'
  }));
  return pool.filter(Boolean);
}

/**
 * @param {{ factType?: string, name?: string }} item
 * @param {Set<string>} types
 * @param {Set<string>} names
 * @returns {boolean}
 */
function railItemUsable(item, types, names) {
  if (!item) return false;
  if (item.factType && types.has(item.factType)) return false;
  if (item.name && names.has(item.name)) return false;
  return true;
}

/**
 * Grid: account share (always), off pace, then rotating slowest tows /
 * vehicle mix / busiest driver / busiest reason. Usual-only tiles skip.
 *
 * @param {'company'|'all'} view
 * @param {object} input
 * @returns {Array<{ title: string, label: string, detail: string }>}
 */
export function buildRail(view, {
  jobs,
  usualJobs,
  todayJobs,
  groups,
  metric = RESPONSE_ON_SCENE,
  policeMinutes,
  regularMinutes,
  accountType,
  now,
  timeZone,
  weekday,
  occupiedTypes = [],
  occupiedNames = [],
  minCalls
} = {}) {
  void view;
  void weekday;
  const windowJobs = jobs || todayJobs || [];
  const types = new Set(occupiedTypes);
  const names = new Set(occupiedNames);
  const slots = [accountShareStat(windowJobs, usualJobs || [])];
  const offPace = offPaceStat({
    groups: groups || [],
    metric,
    policeMinutes,
    regularMinutes,
    accountType,
    now,
    timeZone,
    minCalls
  });
  if (railItemUsable(offPace, types, names)) slots.push(offPace);
  const rotating = [
    slowestTowsStat({
      todayJobs: windowJobs,
      usualJobs: usualJobs || [],
      metric
    }),
    vehicleMixStat({ todayJobs: windowJobs, usualJobs: usualJobs || [] }),
    busiestDriverStat({ jobs: windowJobs }),
    busiestReasonStat({ todayJobs: windowJobs, usualJobs: usualJobs || [] })
  ].filter((item) => railItemUsable(item, types, names));
  for (const item of rotating) {
    if (slots.length >= 4) break;
    if (slots.some((row) => row.title === item.title)) continue;
    slots.push(item);
  }
  return slots.filter(Boolean);
}

/**
 * @param {Date} now
 * @param {string} timeZone
 * @returns {number}
 */
export function highlightWeekday(now, timeZone) {
  return isoWeekdayInTimeZone(now, timeZone);
}

export { currentActivityLine };
